import {CittadinoMensaStudenteDTO} from '../../../../core/api/generated';

/**
 * Chiave d'identità di una RIGA discovery `(figlio, servizio)`. Lo stesso figlio
 * iscritto in più comuni produce più righe con lo stesso `id` ma `servizio.id`
 * diverso: la sola `id` non basta a distinguerle (vedi design D2). Formato:
 * `${id}::${servizio.id}`. Funzione pura, riusata da holder, Hub e child-selector.
 */
export function mensaRowKey(riga: CittadinoMensaStudenteDTO | null | undefined): string {
  if (!riga) return '';
  return `${riga.id ?? ''}::${riga.servizio?.id ?? ''}`;
}

/**
 * Input del resolver puro di selezione riga discovery.
 *
 * - `serviceId`: contesto (non più driver della lista, vedi design D3); informativo.
 * - `children`: lista cross-servizio delle righe `(figlio, servizio)` (verità corrente).
 * - `currentRowKey`: riga attualmente selezionata (suggerimento, non autorevole).
 * - `rememberedRowKey`: riga ricordata. In questo change è sempre `undefined`; lo
 *   alimenterà un eventuale change UX con la memoria per-comune.
 */
export interface ChildSelectionInput {
  serviceId: string;
  children: CittadinoMensaStudenteDTO[];
  currentRowKey?: string;
  rememberedRowKey?: string;
}

/** Risultato del resolver: la `rowKey` da selezionare, o `null` se nessuna selezione. */
export interface ChildSelectionResult {
  rowKey: string | null;
}

/**
 * Risolve la selezione riga da applicare, in modo PURO (nessun side-effect, stesso
 * input → stesso output).
 *
 * Priorità (vedi spec/design D1, D2, D3):
 *   1. `rememberedRowKey` se appartiene a `children`;
 *   2. `currentRowKey` se ancora appartiene a `children`;
 *   3. fallback deterministico: unica riga → quella; più righe → la prima per
 *      ordinamento STABILE (cognome/nome/comune), NON l'ordine accidentale dell'API;
 *      zero righe → nessuna selezione (`null`).
 *
 * La selezione persistita è solo un suggerimento: mai applicata senza validarla
 * contro `children` correnti (invariante D6).
 */
export function resolveChildSelection(input: ChildSelectionInput): ChildSelectionResult {
  const {children, currentRowKey, rememberedRowKey} = input;

  const appartiene = (key?: string): boolean =>
    !!key && children.some((c) => mensaRowKey(c) === key);

  if (appartiene(rememberedRowKey)) {
    return {rowKey: rememberedRowKey!};
  }
  if (appartiene(currentRowKey)) {
    return {rowKey: currentRowKey!};
  }

  if (children.length === 0) {
    return {rowKey: null};
  }
  if (children.length === 1) {
    return {rowKey: mensaRowKey(children[0]) || null};
  }

  const prima = [...children].sort(confrontaRigaStabile)[0];
  return {rowKey: mensaRowKey(prima) || null};
}

/**
 * Ordinamento stabile e deterministico: cognome, poi nome, poi comune. Indipendente
 * dall'ordine di risposta del backend e tale che le righe dello stesso figlio
 * (comuni diversi) risultino ADIACENTI (design D6).
 */
export function confrontaRigaStabile(
  a: CittadinoMensaStudenteDTO,
  b: CittadinoMensaStudenteDTO,
): number {
  return (
    (a.cognome ?? '').localeCompare(b.cognome ?? '') ||
    (a.nome ?? '').localeCompare(b.nome ?? '') ||
    (a.servizio?.comune_nome ?? '').localeCompare(b.servizio?.comune_nome ?? '')
  );
}
